import { isNonNullable } from '@main/shared/utils';
import { FileItemRef, MultipleFilesHandlerResult, StorageErrorPayload } from '@nhost/nhost-js';

export type UploadedFile = {
  id: string;
  name?: string | null;
  size?: number | null;
  mimeType?: string | null;
};

export type MultipleFilesUploadResult = {
  errors: StorageErrorPayload[];
  files: UploadedFile[];
};

export type FileUploadResult =
  | {
      error: Error | StorageErrorPayload;
      file?: undefined;
    }
  | {
      error?: undefined;
      file: UploadedFile;
    };

export function getFileUploadResult({
  errors,
  files,
}: MultipleFilesUploadResult): FileUploadResult {
  if (errors[0]) {
    return { error: errors[0] };
  }
  if (!files[0]) {
    return { error: new Error('File upload was called without any files added') };
  }
  return { file: files[0] };
}

export function getMultipleFilesUploadResult({
  errors,
  files,
}: MultipleFilesHandlerResult): MultipleFilesUploadResult {
  return {
    errors: errors.map(getErrorFromFileRef).filter(isNonNullable),
    files: files.map(getUploadedFileFromFileRef).filter(isNonNullable),
  };
}

function getErrorFromFileRef(fileRef: FileItemRef): StorageErrorPayload | null {
  return fileRef.getSnapshot()?.context.error || null;
}

// MAX_FILE_SIZE is 250MB
export const MAX_FILE_SIZE = 250;

export function isFileSizeOverLimit(size: number, limit = MAX_FILE_SIZE): boolean {
  return size > limit * 1024 * 1024;
}

function getUploadedFileFromFileRef(fileRef: FileItemRef): UploadedFile | null {
  const context = fileRef.getSnapshot()?.context;
  if (!context?.id) {
    return null;
  }
  return {
    id: context.id,
    name: context.file?.name,
    size: context.file?.size,
    mimeType: context.file?.type,
  };
}
